/**
 * The "user sends a message on a session" trigger, shared by every surface (cli
 * TUI, web) so the logic lives in ONE place instead of a per-package copy.
 *
 * NATIVE session (no `sourceId`) → append the prompt + a queued run (RFC 0242
 * enqueueRemoteRun) WITHOUT a local runtime; the managed inference worker drains
 * it. CAPTURED session (external `sourceId`, e.g. a Claude import) → append the
 * message ONLY: its responder is that external source, and a run enqueued here
 * would dangle undrained and pollute a session that syncs out.
 *
 * Depends only on mod-core primitives (repo.find, BranchLog, enqueueRemoteRun), so
 * both `@mod/core`-consuming packages import this rather than duplicating it.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { v5 as uuidv5 } from 'uuid'
import { BranchLog } from '../../log/branch-log'
import { projectStopReports } from '../../log/projection-stops'
import type {
  AttachmentRef,
  EntryId,
  FiringSignal,
  LogDoc,
  MessageSelection,
  MessageView,
  RunDispatch,
  ScheduleDueData,
  StopEvent,
} from '../../log/types'
import { VOICE_RELAYED_EXTENSION } from '../../log/types'
import { sharedSessionEntry } from '../../model/session/catalog'
import { setSessionStatus, touchSession } from '../../model/session/mutations'
import {
  enqueueRemoteRun,
  queueRunForEntry,
  selectionFields,
  viewFields,
} from './run-session'
import { sessionPoster } from './session-poster'

export async function enqueueRun(
  repo: Repo,
  sessionId: string,
  prompt: string,
  opts?: {
    /** The model the user picked in the composer, written as the run's
     *  `requestedModel` so the worker resolves and records exactly it (RFC 0262).
     *  Omitted → the worker falls back to its device/default. */
    requestedModel?: { provider: string; model: string }
    /**
     * Harness / agent id when the run is addressed to a specific driver
     * (e.g. "claude-code" | "codex" | "cursor"). Written on the run so a
     * multi-harness desktop can pick the matching RunHost.
     */
    agent?: string
    /** World skills the composer asked for (RFC 0283 S5), by catalog id. */
    skills?: string[]
    /** RFC 0288: pointers to attachment documents written before this call. */
    attachments?: AttachmentRef[]
    /** RFC 0304 3d: visual view context, not a visible message attachment. */
    viewAttachments?: AttachmentRef[]
    /**
     * RFC 0286 §5a — the session that dispatched this turn and asked to hear
     * back when it ends. Written on the run, so the reply is owed by the turn
     * itself rather than by a record something has to keep in sync with it.
     */
    dispatch?: RunDispatch
    /** RFC 0348 3d: the session whose `send_message` this is, so a World
     *  agent answering one session over and over can be told to stop. */
    sentBy?: string
    /**
     * The project whose registry owns this session. When given, sending marks
     * the session as recently used (`touchSession`).
     *
     * Optional only because not every caller has it in hand, but PASS IT: the
     * daemon hosts the N most-recently-updated sessions, so a session nobody
     * touches drops out of that window and its runs are never claimed. That is
     * the "waiting for a worker" the composer shows, and omitting this is how a
     * surface silently opts its users into it.
     */
    projectId?: string
    /** RFC 0297 §4e: what the person was pointing at when they typed this.
     *  Recorded on the message, so a turn reading the log later still knows
     *  what "this" meant. */
    selection?: MessageSelection | null
    /** RFC 0304 3d: what was visible when the person sent this message. */
    view?: MessageView | null
    /**
     * Causal frontier visible in the sending composer (RFC 0302 S3). Multiple
     * parents deliberately join branches; an empty/absent value uses the live
     * session heads for callers that have no branch-local view.
     */
    parents?: EntryId[]
    /**
     * Run this turn as a BRANCH (RFC 0302 S3): its provider context is the
     * ancestor closure of `parents`, and stays blind to whatever else lands
     * while it waits its place in the queue.
     *
     * Absent is CONTINUE, and continue is the default on purpose. A message
     * arriving while the agent works is usually a correction or a follow-up,
     * and answering one of those from a context that predates the answer it
     * corrects is worse than waiting. Branching is the deliberate case: the
     * person asked for it, or the agent judged the work separable (3c).
     */
    branch?: boolean
  },
): Promise<void> {
  const guest = await sharedSessionEntry(repo, sessionId)
  if (guest && String(guest.level) === 'read')
    throw new Error('You can view this session, but cannot post messages')
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = handle.doc() as
    | { sourceId?: string; sharingGroupId?: unknown }
    | undefined
  // A signed-in sharing repo keeps its person signer even if an editor
  // removes the session's display metadata. Never downgrade to unsigned.
  const post = sessionPoster(repo)
  if (post || doc?.sharingGroupId != null) {
    if (!post)
      throw new Error(
        'A protected session needs this person’s signed-in device',
      )
    await post(sessionId, prompt, opts ?? {})
    // Posting is not permission to spend somebody's inference. Reply routing
    // will separately choose the person's own key or request owner approval.
    if (opts?.projectId && !guest)
      await touchSession(repo, opts.projectId, sessionId)
    return
  }
  const log = new BranchLog(handle as never)
  if (doc?.sourceId || guest) {
    // Captured session: append the message, but no run (external responder).
    log.append(
      {
        type: 'message',
        payload: {
          role: 'user',
          status: 'completed',
          text: prompt,
          toolUses: [],
          ...(opts?.attachments?.length
            ? { attachments: opts.attachments }
            : {}),
          ...(opts?.viewAttachments?.length
            ? { viewAttachments: opts.viewAttachments }
            : {}),
          ...selectionFields(opts?.selection),
          ...viewFields(opts?.view),
        },
      },
      opts?.parents ?? log.heads(),
    )
    // A guest posts to the shared log, not either person's private catalog.
    // Reply approval and selecting the guest's own agent are separate actions.
    if (opts?.projectId && !guest)
      await touchSession(repo, opts.projectId, sessionId)
    return
  }
  enqueueRemoteRun(log, prompt, {
    // The desktop's multi-harness router selects from requestedModel.provider.
    // Agent-only callers (notably start_session/send_message) still promise to
    // address a harness, so materialise that address as a default model choice
    // rather than leaving the router to use the device-wide Claude preference.
    requestedModel:
      opts?.requestedModel ??
      (opts?.agent ? { provider: opts.agent, model: 'default' } : undefined),
    agent: opts?.agent,
    skills: opts?.skills,
    attachments: opts?.attachments,
    viewAttachments: opts?.viewAttachments,
    dispatch: opts?.dispatch,
    sentBy: opts?.sentBy,
    selection: opts?.selection,
    view: opts?.view,
    parents: opts?.parents,
    branch: opts?.branch,
  })
  // AFTER the run is on the log, so the daemon that wakes on this registry write
  // finds the work already there rather than attaching to an empty session and
  // having to be poked again.
  if (opts?.projectId) await announceQueued(repo, opts.projectId, sessionId)
}

/**
 * Tell the REGISTRY this session has work, so a daemon can find out WITHOUT
 * hosting it first.
 *
 * `sessionMeta.status` used to be written only by the host's own status mirror,
 * which is circular: a session nothing hosts can never report that it needs
 * hosting. Hosting therefore had to be speculative — attach the ten most
 * recently touched sessions and look inside each one — which hydrated whole
 * logs that had nothing to drain, and still missed a session with a real queued
 * run as soon as ten newer ones existed.
 *
 * The enqueuer already knows a run exists, because it just wrote one. Saying so
 * here costs one registry write and opens no session document, and it is what
 * lets `sessionsToHost` select on fact rather than on a guess.
 *
 * `setSessionStatus` refuses to move a status backwards by timestamp, so a
 * host's later `running` is not undone by a straggling write from here.
 */
async function announceQueued(
  repo: Repo,
  projectId: string,
  sessionId: string,
): Promise<void> {
  await touchSession(repo, projectId, sessionId)
  await setSessionStatus(repo, projectId, sessionId, 'queued')
}

/**
 * RFC 0286 §5a — deliver a dispatch reply to the session that asked for it: the
 * `mod.session_replied` event, plus a QUEUED run so it is actually picked up.
 *
 * The same append-plus-queued-run path as `enqueueRun`, minus the user message,
 * and it lives beside it for that reason: sending a message and being told about
 * an answer are the same delivery mechanism, and the only difference — an event
 * rather than a person's words — is the one thing §5a insists on. A queued run
 * is also why a busy sender is never interrupted: it sits `queued` until that
 * session is next free, exactly like any other message it was sent.
 *
 * Returns false when the sender is a CAPTURED session: its responder is an
 * external process that does not drain mod runs, so the event is still recorded
 * (the reply is on the log, readable by anyone) but no run is queued to dangle.
 */
export async function enqueueReplyNotification(
  repo: Repo,
  senderSessionId: string,
  reply: {
    /** The session that answered, and the run of its turn that just ended. */
    sessionId: string
    runId: string
    dispatchId: string
    summary: string
    at?: number
  },
): Promise<boolean> {
  return enqueueNotification(repo, senderSessionId, (log) =>
    log.appendSessionReplied(reply),
  )
}

/** One stop the World agent heard, as its host writes it (RFC 0348 3a). */
export interface HeardStop {
  sessionId: string
  runId: string
  /** RFC 0348 3h: which event this is. */
  event?: StopEvent
  status: 'completed' | 'failed' | 'cancelled' | 'asking'
  interactionId?: string
  /** The first line of what it said, or its question. */
  summary: string
  /** How often in a row the World agent has answered this session. */
  answeredInARow?: number
  /** The person had it open (RFC 0348 3b): written, and no turn for it. */
  seen?: boolean
  at?: number
}

/**
 * Record stops the World agent has heard on its OWN log, and queue the one
 * turn that decides them, returning whether a turn was queued (RFC 0348 3a,
 * 3c).
 *
 * Written by the World agent's host, which read them off the registries: no
 * session writes here. One turn at most waits: a queued turn has not read the
 * log yet, so it reads these too. A turn already running read its prompt when
 * it began, so these need the next one.
 */
export function hearStops(
  log: BranchLog,
  stops: readonly HeardStop[],
): boolean {
  if (stops.length === 0) return false
  let last: EntryId | null = null
  for (const stop of stops) {
    const id = log.appendSessionStopped(stop)
    if (!stop.seen) last = id
  }
  if (!last) return false
  const queued = log
    .entries()
    .some(
      (e) =>
        e.payload.type === 'run' &&
        String(e.payload.payload.status) === 'queued',
    )
  if (queued) return false
  queueRunForEntry(log, last, previousRunAddress(log))
  return true
}

/**
 * RFC 0350 3d: put a due reminder on one subscriber's log, and give the
 * woken one a turn. Idempotent on the occurrence key, both halves: the event
 * is written once (`appendScheduleDue`), and its turn is queued once, so a
 * clock retrying a delivery after a crash adds nothing that already landed.
 *
 * Like `hearStops`, one queued turn is enough: a turn that has not started
 * reads this event too. `captured` sessions get the event and no turn.
 */
export function hearReminder(
  log: BranchLog,
  reminder: Omit<ScheduleDueData, 'at'>,
  opts: { captured?: boolean } = {},
): { entryId: EntryId; queued: boolean } {
  const { entryId } = log.appendScheduleDue(reminder)
  if (!reminder.wake || opts.captured) return { entryId, queued: false }
  const already = log.entries().some((e) => {
    if (e.payload.type !== 'run') return false
    const run = e.payload.payload
    return (
      String(run.promptMessageId) === entryId || String(run.status) === 'queued'
    )
  })
  if (already) return { entryId, queued: false }
  queueRunForEntry(log, entryId, {
    ...previousRunAddress(log),
    runId: uuidv5(reminder.key, '0feb13c6-5351-4310-91e3-eb75876b6d1b'),
  })
  return { entryId, queued: true }
}

/** How many World agent turns may read one report without deciding it before
 *  it is left: a harness that fails every turn must not queue one forever. */
export const MAX_TURNS_PER_REPORT = 2

/**
 * Give the World agent another turn for reports it still owes, and return
 * whether one was queued (RFC 0348 3c).
 *
 * A report is owed until `decide_stop` covers it, unless the person had the
 * session open. A turn that ended without deciding it (cut short by a restart,
 * or one that forgot) leaves it owed, so once nothing is queued or running the
 * report gets the next turn, at most `MAX_TURNS_PER_REPORT` turns after it
 * landed. That turn's prompt reads every owed report wherever it sits.
 */
export function ensureOwedTurn(log: BranchLog): boolean {
  const entries = log.entries()
  const doc = {
    entries: Object.fromEntries(entries.map((e) => [e.id, e])),
  } as unknown as LogDoc
  const owed = projectStopReports(doc).filter(
    (r) => !r.verdict && !r.seen && r.status !== 'started',
  )
  if (owed.length === 0) return false
  const landed = new Map(entries.map((e) => [String(e.id), e.createdAt]))
  const runs = entries.filter((e) => e.payload.type === 'run')
  for (const e of runs) {
    if (e.payload.type !== 'run') continue
    const status = String(e.payload.payload.status)
    if (
      status === 'queued' ||
      status === 'running' ||
      status === 'waiting_for_tools'
    )
      return false
  }
  const first = Math.min(...owed.map((r) => landed.get(r.entryId) ?? 0))
  const turnsSince = runs.filter((e) => {
    if (e.payload.type !== 'run') return false
    return (e.payload.payload.startedAt || e.createdAt) >= first
  }).length
  if (turnsSince >= MAX_TURNS_PER_REPORT) return false
  const newest = owed.reduce((a, r) =>
    (landed.get(r.entryId) ?? 0) >= (landed.get(a.entryId) ?? 0) ? r : a,
  )
  queueRunForEntry(log, newest.entryId as EntryId, previousRunAddress(log))
  return true
}

/**
 * RFC 0289 — tell a session that someone answered a comment it wrote, and
 * queue the turn that answers back.
 *
 * The same append-plus-queued-run path as `enqueueReplyNotification`, and it
 * sits beside it for the same reason: being told a dispatched turn finished and
 * being told a comment was answered are one delivery mechanism, differing only
 * in what happened. The queued run is also why a busy session is never
 * interrupted; the turn sits `queued` until that session is next free.
 *
 * Returns false for a CAPTURED session: its responder is an external process
 * that does not drain mod runs, so the event is still recorded (anyone reading
 * the log sees the reply happened) but no run is queued to dangle undrained.
 */
/**
 * RFC 0292 — tell a session it was NAMED in a body of prose, and queue the turn
 * that answers.
 *
 * The third caller of the same append-plus-queued-run path, beside a dispatch
 * reply and a comment reply, and it sits here for the reason the other two do:
 * being told something happened elsewhere is one delivery mechanism, and the
 * only difference is what happened. The queued run is why a busy session is
 * never interrupted — the turn sits `queued` until that session is next free.
 *
 * Returns false for a CAPTURED session, whose responder is an external process
 * that does not drain mod runs: the event is still recorded, so anyone reading
 * the log sees the mention, but no run is queued to dangle undrained.
 */
export async function enqueueMentionNotification(
  repo: Repo,
  sessionId: string,
  mention: {
    commentId: string
    documentId?: string
    author: string
    anchorText?: string
    nodeId?: string
    summary: string
    at?: number
  },
  opts?: {
    /** PASS IT, for the reason `enqueueRun` gives: the daemon hosts the N
     *  most-recently-updated sessions, so naming a session nobody has touched
     *  in a week queues a run nothing is hosting, and the only symptom is
     *  "waiting for a worker" forever. */
    projectId?: string
  },
): Promise<boolean> {
  return enqueueNotification(
    repo,
    sessionId,
    (log) => log.appendMentioned(mention),
    opts,
  )
}

/**
 * Tell a session something happened, and queue the turn that answers (RFC 0297
 * §6b S0).
 *
 * THE REASON IS AN ARGUMENT. Three callers wrote this same seven lines with one
 * word different: a dispatch reply, a comment reply, a mention. Each knew which
 * entry to append and nothing else, and each carried its own copy of the two
 * rules that are easy to get wrong — a captured session records but queues
 * nothing, and a session nobody has touched needs touching or the run dangles.
 * A fourth reason is coming (a hook firing, §4g) and a fifth after it, so the
 * copy stops here.
 *
 * `append` is handed the log and answers with the entry it wrote. That is the
 * whole of what differs, which is why it is the whole of the parameter.
 *
 * Returns false for a CAPTURED session, whose responder is an external process
 * that does not drain mod runs: the event is still on the log, so anyone
 * reading it sees what happened, and no run is queued to dangle undrained.
 */
export async function enqueueNotification(
  repo: Repo,
  sessionId: string,
  append: (log: BranchLog) => string,
  opts?: { projectId?: string },
): Promise<boolean> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = handle.doc() as { sourceId?: string } | undefined
  const log = new BranchLog(handle as never)
  const entryId = append(log)
  if (doc?.sourceId) return false
  queueRunForEntry(log, entryId, previousRunAddress(log))
  if (opts?.projectId) await announceQueued(repo, opts.projectId, sessionId)
  return true
}

/**
 * Notifications are another turn in the same conversation, so they keep the
 * harness/model the person most recently addressed instead of falling back to
 * the machine-wide harness preference. Skip legacy unaddressed notification
 * runs (`agent: "default"`) so one old firing cannot erase that choice.
 */
function previousRunAddress(log: BranchLog): {
  agent?: string
  requestedModel?: { provider: string; model: string }
} {
  const entries = log
    .entries()
    .filter((entry) => String(entry.payload.type) === 'run')
    .sort(
      (a, b) => b.createdAt - a.createdAt || String(b.id).localeCompare(a.id),
    )
  for (const entry of entries) {
    const run = entry.payload.payload as {
      agent?: unknown
      requestedModel?: unknown
    }
    const agent = run.agent == null ? '' : String(run.agent)
    const selected = run.requestedModel as
      | { provider?: unknown; model?: unknown }
      | undefined
    const provider = selected?.provider == null ? '' : String(selected.provider)
    const model = selected?.model == null ? '' : String(selected.model)
    if (agent === 'default' && (!provider || !model)) continue
    return {
      ...(agent && agent !== 'default' ? { agent } : {}),
      ...(provider && model
        ? { requestedModel: { provider, model } }
        : agent && agent !== 'default'
          ? { requestedModel: { provider: agent, model: 'default' } }
          : {}),
    }
  }
  return {}
}

export async function enqueueCommentReplyNotification(
  repo: Repo,
  sessionId: string,
  reply: {
    commentId: string
    parentId: string
    documentId?: string
    author: string
    anchorText?: string
    nodeId?: string
    summary: string
    at?: number
  },
  opts?: {
    /**
     * PASS IT, for the reason `enqueueRun` gives: the daemon hosts the N
     * most-recently-updated sessions, so a reply to a comment written last week
     * queues a run on a session nothing is hosting, and the only symptom is the
     * composer saying "waiting for a worker" forever.
     */
    projectId?: string
  },
): Promise<boolean> {
  return enqueueNotification(
    repo,
    sessionId,
    (log) => log.appendCommentReplied(reply),
    opts,
  )
}

/**
 * A signal came through a pairing: append it, note the firing, queue the turn
 * (RFC 0301 §3f).
 *
 * The fourth caller of `enqueueNotification`, and the only difference from the
 * other three is the `mod.fired` note beside the signal. `appendSignal` writes
 * the signal's own entry (a handover, a claimed comment), which stays the
 * turn's prompt so the agent reads what happened rather than that a pairing
 * fired. The gate runs BEFORE this; nothing here decides whether to fire.
 */
export async function enqueueFiring(
  repo: Repo,
  sessionId: string,
  firing: {
    pairingId: string
    documentId: string
    signal: FiringSignal
  },
  appendSignal: (log: BranchLog) => EntryId,
  opts?: { projectId?: string },
): Promise<boolean> {
  return enqueueNotification(
    repo,
    sessionId,
    (log) => {
      const entryId = appendSignal(log)
      log.appendFired({ ...firing, entryId })
      return entryId
    },
    opts,
  )
}

/**
 * Write one line of a voice call to the session (RFC 0307 3c).
 *
 * A MESSAGE and nothing else: no run is queued, so no worker wakes. The thread
 * shows it; the agent reads it as context on its next turn. Only
 * `relaySpokenLine` turns a person's line into a prompt.
 */
export async function appendSpokenLine(
  repo: Repo,
  sessionId: string,
  line: { voiceCallId: string; by: 'person' | 'voice'; text: string },
): Promise<EntryId> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const log = new BranchLog(handle as never)
  return log.append(
    {
      type: 'message',
      payload: {
        // The voice's line is not the agent's reply, so it is never written as
        // `assistant`: every reader of "the last assistant message" would take
        // it for one.
        role: line.by === 'person' ? 'user' : 'system',
        status: 'completed',
        text: line.text,
        toolUses: [],
        voiceCallId: line.voiceCallId,
        spokenBy: line.by,
      },
    },
    log.heads(),
  )
}

/** A session that answers through an external source never drains a run. */
function refuseCaptured(handle: unknown): void {
  const doc = (handle as { doc(): unknown }).doc() as
    | { sourceId?: string }
    | undefined
  if (doc?.sourceId)
    throw new Error('a captured session cannot be sent work by voice')
}

/** The message a person said on a call, or a refusal naming what it is not. */
function personLine(log: BranchLog, entryId: EntryId) {
  const entry = log.get(entryId)
  const p = entry?.payload
  if (
    p?.type !== 'message' ||
    !p.payload.voiceCallId ||
    String(p.payload.spokenBy ?? 'person') !== 'person'
  )
    throw new Error(`${entryId} is not a line the person said on a call`)
  return p.payload
}

/**
 * Send a person's spoken line to an agent (RFC 0307 3b, `tell_agent`).
 *
 * At HOME, a run is queued on the line already on the log, so the agent gets
 * the person's own words with no second copy of them. Sent `to` a session the
 * call's agent handed work to, the exact words are copied there with a
 * pointer back to the call, a run is queued there, and a small extension at
 * home records where the line went. Either way `relayed` is projected from
 * the log rather than stored as a flag on the spoken message. Queues behind a
 * busy session like any message (RFC 0302), and sending the same line twice
 * returns the run the first send queued.
 */
export async function relaySpokenLine(
  repo: Repo,
  sessionId: string,
  entryId: EntryId,
  opts: {
    projectId?: string
    requestedModel?: { provider: string; model: string }
    agent?: string
    /** A followed session; omitted or the same id means home. */
    to?: string
  } = {},
): Promise<EntryId> {
  const homeHandle = await repo.find(sessionId as AnyDocumentId)
  await (homeHandle as { whenReady?: () => Promise<void> }).whenReady?.()
  const homeLog = new BranchLog(homeHandle as never)
  const line = personLine(homeLog, entryId)
  const target = opts.to && opts.to !== sessionId ? opts.to : null

  if (!target) {
    refuseCaptured(homeHandle)
    for (const e of homeLog.entries()) {
      if (
        e.payload.type === 'run' &&
        e.payload.payload.promptMessageId === entryId
      )
        return e.id
    }
    const started = queueRunForEntry(homeLog, entryId, {
      requestedModel:
        opts.requestedModel ??
        (opts.agent ? { provider: opts.agent, model: 'default' } : undefined),
      agent: opts.agent,
    })
    if (opts.projectId) await announceQueued(repo, opts.projectId, sessionId)
    return started.runId
  }

  const targetHandle = await repo.find(target as AnyDocumentId)
  await (targetHandle as { whenReady?: () => Promise<void> }).whenReady?.()
  refuseCaptured(targetHandle)
  const targetLog = new BranchLog(targetHandle as never)
  const prior = targetLog
    .entries()
    .find(
      (e) =>
        e.payload.type === 'message' &&
        e.payload.payload.voiceHomeSessionId === sessionId &&
        e.payload.payload.voiceHomeEntryId === entryId,
    )
  const targetEntryId =
    prior?.id ??
    targetLog.append(
      {
        type: 'message',
        payload: {
          role: 'user',
          status: 'completed',
          text: String(line.text),
          toolUses: [],
          voiceCallId: String(line.voiceCallId),
          spokenBy: 'person',
          voiceHomeSessionId: sessionId,
          voiceHomeEntryId: entryId,
        },
      },
      targetLog.heads(),
    )
  const existingRun = targetLog
    .entries()
    .find(
      (e) =>
        e.payload.type === 'run' &&
        e.payload.payload.promptMessageId === targetEntryId,
    )
  const runId =
    existingRun?.id ??
    queueRunForEntry(targetLog, targetEntryId, previousRunAddress(targetLog))
      .runId
  if (!existingRun && opts.projectId)
    await announceQueued(repo, opts.projectId, target)

  const marked = homeLog
    .entries()
    .some(
      (e) =>
        e.payload.type === 'extension' &&
        e.payload.payload.extensionType === VOICE_RELAYED_EXTENSION &&
        String(e.payload.payload.payload.sourceEntryId) === entryId &&
        String(e.payload.payload.payload.targetSessionId) === target,
    )
  if (!marked)
    homeLog.append(
      {
        type: 'extension',
        payload: {
          extensionType: VOICE_RELAYED_EXTENSION,
          version: 1,
          payload: {
            sourceEntryId: entryId,
            targetSessionId: target,
            targetEntryId,
          },
        },
      },
      homeLog.heads(),
    )
  return runId
}
